<script setup lang="ts">
import { computed, ref } from "vue";
import SqlBlock from "./SqlBlock.vue";

export interface QueryLogEntry {
  id: string;
  sql: string;
  params: ReadonlyArray<unknown>;
  startedAt: number;
  status: "pending" | "ok" | "error";
  rows?: number;
  durationMs?: number;
  errorMessage?: string;
  errorCode?: string;
  /**
   * True when photon served this statement from its cache.
   *
   * Three-valued on purpose. `undefined` means "the server didn't say" —
   * either an older insights-api or an error line — and renders as no badge
   * at all, which is different from a known miss. Deployments that run
   * `PHOTON_CACHE_URL=none://` report a genuine `false` on every query, so a
   * MISS badge on every row would be permanent noise; only hits are badged.
   */
  cached?: boolean;
}

const props = defineProps<{
  queries: QueryLogEntry[];
}>();

/**
 * Slow-query threshold in milliseconds. Queries that exceed this fire
 * the `data-slow` row attribute, which CSS picks up to tint the border
 * amber. Chosen empirically: most parameterised warehouse queries
 * against the runtime's typical schemas land sub-100ms; anything over
 * half a second is worth a glance from the DS persona. Lives at the
 * top of the file so it's findable when the number needs tuning.
 */
const SLOW_QUERY_THRESHOLD_MS = 500;

// Header controls — search filter narrows the visible rows; the inline-
// params toggle swaps every SqlBlock between the raw `$N` form and the
// substituted form. Both default to "off" so a freshly-opened tab looks
// the same as before this change.
const searchText = ref<string>("");
const inlineParams = ref<boolean>(false);

/**
 * Render a param tuple for the Queries tab. JSON.stringify with a tight
 * indent gives us readable output for scalars, arrays, and dates (Date
 * objects flatten to ISO strings via their default toJSON). Anything
 * non-serialisable falls back to `String(value)`.
 */
const formatParams = (params: ReadonlyArray<unknown>): string => {
  if (params.length === 0) return "(no params)";
  return params
    .map((p, i) => {
      let rendered: string;
      try {
        rendered = JSON.stringify(p);
      } catch {
        rendered = String(p);
      }
      return `$${i + 1} = ${rendered}`;
    })
    .join("\n");
};

/**
 * Render a single param value as a SQL literal. Best-effort substitution
 * so the copied query is runnable in a SQL prompt:
 *
 *   - null / undefined         → NULL
 *   - boolean                  → TRUE / FALSE
 *   - finite number / bigint   → bare digits
 *   - non-finite number        → NULL (NaN / ±Infinity have no SQL form)
 *   - Date                     → single-quoted ISO string
 *   - string                   → single-quoted, embedded `'` doubled
 *   - array                    → (elem, elem, …) — works for IN, ARRAY[…]
 *   - everything else          → single-quoted JSON.stringify fallback
 *
 * This is "paste into a query tool" fidelity, not "round-trip into the
 * worker". Edge case: `$1` substrings inside string literals get inlined
 * along with the placeholder, but the regex pass at the caller is
 * single-shot so substituted values are never re-scanned for further
 * `$N` matches.
 */
const renderParamLiteral = (v: unknown): string => {
  if (v === null || v === undefined) return "NULL";
  if (typeof v === "boolean") return v ? "TRUE" : "FALSE";
  if (typeof v === "number")
    return Number.isFinite(v) ? String(v) : "NULL";
  if (typeof v === "bigint") return v.toString();
  if (v instanceof Date) {
    const iso = Number.isNaN(v.getTime()) ? "" : v.toISOString();
    return `'${iso}'`;
  }
  if (typeof v === "string") return `'${v.replace(/'/g, "''")}'`;
  if (Array.isArray(v)) {
    return `(${v.map(renderParamLiteral).join(", ")})`;
  }
  try {
    return `'${JSON.stringify(v).replace(/'/g, "''")}'`;
  } catch {
    return `'${String(v).replace(/'/g, "''")}'`;
  }
};

/**
 * Inline positional params into a `$1` / `$2` / … placeholder SQL string.
 * Single regex pass — replaces `$N` for every N that the regex finds,
 * so `$10` is treated as one placeholder rather than `$1` followed by
 * `0`. Out-of-range placeholders are left as-is so the DS can spot the
 * mismatch instead of getting a silently broken query.
 */
const renderSqlWithParams = (
  sql: string,
  params: ReadonlyArray<unknown>,
): string =>
  sql.replace(/\$(\d+)/g, (match, n: string): string => {
    const idx = Number.parseInt(n, 10) - 1;
    if (idx < 0 || idx >= params.length) return match;
    return renderParamLiteral(params[idx]);
  });

/**
 * Normalise a SQL string for duplicate detection. Replaces positional
 * placeholders with `?` and collapses whitespace so queries that differ
 * only in their `$N` bindings — or in trailing newlines from however
 * the SBI source emitted them — hash to the same key.
 *
 * Doesn't mask inline string/number literals: the SBI runtime
 * parameterises everything, so literals only appear when an author has
 * hand-written them, and treating two literal `WHERE status = 'OK'`
 * queries as duplicates is what the DS would want anyway. If they ever
 * did diverge by inline literal alone, the duplicate count would
 * under-count rather than mis-group, which is the safer failure mode.
 */
const normalizeSql = (sql: string): string =>
  sql.replace(/\$\d+/g, "?").replace(/\s+/g, " ").trim();

/**
 * Per-shape duplicate counts over the FULL prop, not the filtered
 * view. A query that ran 3 times but only matches the search filter
 * once should still badge "×3" — the badge is a property of the
 * query shape, not of the visible row count.
 */
const duplicateCounts = computed<Map<string, number>>(() => {
  const m = new Map<string, number>();
  for (const q of props.queries) {
    const k = normalizeSql(q.sql);
    m.set(k, (m.get(k) ?? 0) + 1);
  }
  return m;
});

const duplicateCountFor = (entry: QueryLogEntry): number =>
  duplicateCounts.value.get(normalizeSql(entry.sql)) ?? 1;

/**
 * Original 1-based position of each query within the unfiltered list,
 * keyed by id. We need this so the `#N` index stamp keeps pointing at
 * the query's true position even after search filtering reorders /
 * removes rows — otherwise scrolling the filtered list past a `#3`
 * makes you wonder whether `#3` means the third query overall or the
 * third match. Computed once per `queries` change; lookups are O(1).
 */
const originalIndex = computed<Map<string, number>>(() => {
  const m = new Map<string, number>();
  props.queries.forEach((q, i) => m.set(q.id, i + 1));
  return m;
});

/**
 * Header aggregates over the full list. Search filtering deliberately
 * doesn't change what "12 queries" means — the header is the panel's
 * summary, not the visible-rows summary.
 *
 * `elapsedMs` is the **wall-clock** span between the earliest query
 * start and the latest query end. The runtime's `dispatchBatchStream`
 * fires queries in parallel batches, so a naïve sum of per-query
 * `durationMs` would overstate the time the user actually waited by
 * roughly the parallelism factor. Wall-clock answers the question the
 * DS actually has ("how long did this insight block on data?") instead
 * of an unrelated CPU-time-style aggregate.
 *
 * `slowestMs` is complementary: it points at the single query that
 * bottlenecked the wall-clock window, which is usually where the DS's
 * optimisation attention should land first. Both stats skip pending
 * queries (no duration yet) but include errors — an erroring query
 * that took 4s before failing is exactly what we want surfaced.
 */
const stats = computed(() => {
  let minStart = Number.POSITIVE_INFINITY;
  let maxEnd = 0;
  let errorCount = 0;
  let slowestMs = 0;
  let cachedCount = 0;
  let cacheReported = false;
  for (const q of props.queries) {
    if (q.durationMs !== undefined) {
      if (q.startedAt < minStart) minStart = q.startedAt;
      const end = q.startedAt + q.durationMs;
      if (end > maxEnd) maxEnd = end;
      if (q.durationMs > slowestMs) slowestMs = q.durationMs;
    }
    if (q.status === "error") errorCount += 1;
    // Tracked separately from the count so "0 cached" (a real, informative
    // all-miss pass) is distinguishable from "this server never told us",
    // which should show nothing at all.
    if (q.cached !== undefined) {
      cacheReported = true;
      if (q.cached) cachedCount += 1;
    }
  }
  const elapsedMs =
    minStart === Number.POSITIVE_INFINITY ? 0 : Math.round(maxEnd - minStart);
  return {
    total: props.queries.length,
    uniqueCount: duplicateCounts.value.size,
    elapsedMs,
    errorCount,
    slowestMs,
    cachedCount,
    cacheReported,
  };
});

/**
 * Search filter — case-insensitive substring match on the raw SQL
 * text. Deliberately searches the `$N` form rather than the
 * substituted form, so the DS can grep for a table name without their
 * inline-params toggle changing what's findable.
 */
const visibleQueries = computed<QueryLogEntry[]>(() => {
  const q = searchText.value.trim().toLowerCase();
  if (!q) return props.queries;
  return props.queries.filter((entry) => entry.sql.toLowerCase().includes(q));
});

/**
 * Slow-row predicate. Returns false for queries that haven't reported
 * a duration yet (pending), so the row tint only fires when there's
 * actually a number to compare.
 */
const isSlow = (durationMs: number | undefined): boolean =>
  durationMs !== undefined && durationMs > SLOW_QUERY_THRESHOLD_MS;

// Per-row "Copied" flash, keyed by QueryLogEntry.id so multiple buttons
// can flash independently. Entries auto-evict after the flash window so
// the ref doesn't grow unboundedly across a long-lived embed.
const copiedFlash = ref<Set<string>>(new Set());

const copyQuery = async (entry: QueryLogEntry): Promise<void> => {
  const text = renderSqlWithParams(entry.sql, entry.params);
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    // Clipboard API can throw on insecure contexts (http://) or when
    // the document isn't focused. Swallow — there's no useful surface
    // to show the user from inside the embed chrome, and pretending we
    // copied is worse than the button just not flashing.
    return;
  }
  const next = new Set(copiedFlash.value);
  next.add(entry.id);
  copiedFlash.value = next;
  setTimeout(() => {
    if (!copiedFlash.value.has(entry.id)) return;
    const after = new Set(copiedFlash.value);
    after.delete(entry.id);
    copiedFlash.value = after;
  }, 1500);
};
</script>

<template>
  <div class="chrome-tab-body queries-panel" role="tabpanel">
    <p v-if="!queries.length" class="queries-empty">
      No queries dispatched yet.
    </p>
    <template v-else>
      <!--
        Header bar — aggregates over the full list on top, controls
        underneath. Stats deliberately don't react to the search
        filter; "12 queries" is the panel's summary, not the visible-
        rows summary. The header is hidden entirely until the panel
        has at least one query, so the empty state stays uncluttered.
      -->
      <div class="queries-header">
        <div class="queries-stats" role="status" aria-live="polite">
          <span class="queries-stat">
            <strong>{{ stats.total }}</strong>
            {{ stats.total === 1 ? "query" : "queries" }}
          </span>
          <span class="queries-stat-sep" aria-hidden="true">·</span>
          <span
            class="queries-stat"
            title="Wall-clock elapsed from the earliest query start to the latest query end. Queries dispatch in parallel batches, so this is the time the viewer actually waited — not the sum of per-query durations."
          >
            <strong>{{ stats.elapsedMs }}</strong>ms elapsed
          </span>
          <span class="queries-stat-sep" aria-hidden="true">·</span>
          <span class="queries-stat">
            <strong>{{ stats.uniqueCount }}</strong> unique
          </span>
          <!--
            Cache hit count. Hidden entirely when no line carried a verdict
            so an older server renders the header exactly as before. A
            persistent `0 cached` is itself the signal worth having: it's
            what a per-replica memory cache spread across N replicas looks
            like, and what `PHOTON_CACHE_URL=none://` looks like too.
          -->
          <template v-if="stats.cacheReported">
            <span class="queries-stat-sep" aria-hidden="true">·</span>
            <span
              class="queries-stat"
              :class="{ 'queries-stat-cached': stats.cachedCount > 0 }"
              title="Queries the data plane served from its cache rather than re-executing. A run where this stays at 0 across repeated views means the cache isn't being hit — worth checking whether it's shared across replicas."
            >
              <strong>{{ stats.cachedCount }}</strong> cached
            </span>
          </template>
          <template v-if="stats.errorCount > 0">
            <span class="queries-stat-sep" aria-hidden="true">·</span>
            <span class="queries-stat queries-stat-error">
              <strong>{{ stats.errorCount }}</strong>
              error{{ stats.errorCount === 1 ? "" : "s" }}
            </span>
          </template>
          <template v-if="stats.slowestMs > 0">
            <span class="queries-stat-sep" aria-hidden="true">·</span>
            <span
              class="queries-stat"
              :class="{ 'queries-stat-slow': isSlow(stats.slowestMs) }"
            >
              slowest <strong>{{ stats.slowestMs }}</strong>ms
            </span>
          </template>
        </div>
        <div class="queries-controls">
          <label class="queries-search" :class="{ active: searchText.length > 0 }">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7"/><line x1="20" y1="20" x2="16.6" y2="16.6"/></svg>
            <input
              v-model="searchText"
              type="search"
              class="queries-search-input"
              placeholder="Filter queries…"
              aria-label="Filter queries by SQL text"
              autocomplete="off"
              spellcheck="false"
            />
            <button
              v-if="searchText"
              type="button"
              class="queries-search-clear"
              aria-label="Clear search"
              @click.prevent="searchText = ''"
            >
              ×
            </button>
          </label>
          <!--
            Global "inline params" toggle. When on, every row's SqlBlock
            renders the substituted form (via the existing
            `renderSqlWithParams` helper) instead of the raw `$N` form,
            and the per-row `$N = value` summary block hides because
            it'd be redundant. Default off — viewers who don't care
            about params get the more compact, more grep-able default.
          -->
          <label class="queries-toggle">
            <input v-model="inlineParams" type="checkbox" />
            <span>Inline params</span>
          </label>
        </div>
      </div>

      <p
        v-if="visibleQueries.length === 0"
        class="queries-empty queries-empty-filtered"
      >
        No queries match <code>{{ searchText.trim() }}</code>.
      </p>
      <ol v-else class="queries-list">
        <li
          v-for="q in visibleQueries"
          :key="q.id"
          class="query-entry"
          :data-status="q.status"
          :data-slow="isSlow(q.durationMs) ? 'true' : null"
        >
          <div class="query-entry-meta">
            <span class="query-entry-idx">#{{ originalIndex.get(q.id) }}</span>
            <span class="query-entry-status">{{ q.status }}</span>
            <span v-if="q.durationMs !== undefined" class="query-entry-dim">
              {{ q.durationMs }}ms
            </span>
            <span
              v-if="q.status === 'ok' && q.rows !== undefined"
              class="query-entry-dim"
            >
              {{ q.rows }} row{{ q.rows === 1 ? "" : "s" }}
            </span>
            <span v-if="q.errorCode" class="query-entry-dim">
              {{ q.errorCode }}
            </span>
            <!--
              Duplicate badge — pinned next to the meta when the
              query's normalized shape ran more than once across the
              full panel. The count is over the full prop, not the
              filtered view, so it matches the "Σ N unique" stat in
              the header. Click semantics: none for now — Symfony's
              panel makes the badge a filter trigger, which we'd add
              if the volume of duplicates ever justifies it.
            -->
            <span
              v-if="duplicateCountFor(q) > 1"
              class="query-entry-dup"
              :title="`This query shape ran ${duplicateCountFor(q)} times`"
            >
              ×{{ duplicateCountFor(q) }}
            </span>
            <!--
              Cache-hit badge. Hits only — a MISS badge on every row would be
              permanent noise on any deployment with caching switched off,
              and `undefined` (older server, or an error line) must not read
              as a miss either. Note this doesn't corroborate `durationMs`
              above it: that number is browser round-trip time including the
              network, so a hit isn't necessarily a fast row.
            -->
            <span
              v-if="q.cached === true"
              class="query-entry-cached"
              title="Served from the data plane's cache — this statement was not re-executed. The duration beside it is browser round-trip time, which includes the network either way."
            >
              cached
            </span>
            <!--
              Copy button — inlines the positional params into the SQL so
              the clipboard contents are pasteable directly into a SQL
              prompt. Sits at the end of the meta row (margin-left: auto)
              so it tracks the right edge regardless of how much meta is
              shown. Flashes "Copied" for 1.5s on success; on failure
              (insecure context, document unfocused) the button just
              doesn't flash and no error surfaces — clipboard failure
              here isn't user-actionable.
            -->
            <button
              type="button"
              class="query-entry-copy"
              :aria-label="
                copiedFlash.has(q.id)
                  ? 'SQL copied to clipboard'
                  : 'Copy SQL with params'
              "
              @click="copyQuery(q)"
            >
              {{ copiedFlash.has(q.id) ? "Copied" : "Copy SQL" }}
            </button>
          </div>
          <pre class="query-entry-sql"><SqlBlock :sql="inlineParams ? renderSqlWithParams(q.sql, q.params) : q.sql" /></pre>
          <pre
            v-if="!inlineParams && q.params.length"
            class="query-entry-params"
          ><code>{{ formatParams(q.params) }}</code></pre>
          <p v-if="q.errorMessage" class="query-entry-error">
            {{ q.errorMessage }}
          </p>
        </li>
      </ol>
    </template>
  </div>
</template>

<style scoped>
.queries-panel {
  padding: 8px 0 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.queries-empty {
  margin: 0;
  padding: 16px 4px;
  color: var(--card-muted, #6b7280);
  font-size: 13px;
}
.queries-empty-filtered code {
  font-family: var(--mono, ui-monospace, monospace);
  font-size: 12px;
  padding: 1px 6px;
  border-radius: 4px;
  background: var(--rule-soft, rgba(0, 0, 0, 0.05));
}

/* Header bar — stats row on top, controls row below. Wraps onto more
 * rows automatically on narrow widths because each child uses
 * `flex-wrap: wrap`. The two rows share `gap` rather than padding so
 * adjacent stats hug the bullet separator visually. */
.queries-header {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 12px;
  background: var(--card, #fff);
  border: 1px solid var(--rule, #e5e7eb);
  border-radius: var(--radius, 8px);
}
.queries-stats {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 6px 8px;
  font-size: 12px;
  color: var(--card-muted, #6b7280);
  font-family: var(--mono, ui-monospace, monospace);
}
.queries-stat strong {
  color: var(--card-ink, #111);
  font-weight: 600;
}
.queries-stat-sep {
  color: var(--card-muted, #6b7280);
  opacity: 0.55;
}
/* Error and slow stats lift the leading number to a semantic tint —
 * everything else stays subdued so the header doesn't shout at you
 * when nothing's wrong. */
.queries-stat-error strong {
  color: var(--neg, #b00020);
}
.queries-stat-slow strong {
  color: #b45309;
}

/* Controls row: search input grows to fill, toggle pins right. */
.queries-controls {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
}
.queries-search {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 10px;
  background: var(--card, #fff);
  border: 1px solid var(--rule, #e5e7eb);
  border-radius: 6px;
  font-size: 12px;
  flex: 1 1 200px;
  min-width: 0;
  color: var(--card-muted, #6b7280);
  cursor: text;
  transition:
    border-color 120ms,
    box-shadow 120ms;
}
.queries-search:hover {
  border-color: color-mix(in srgb, var(--card-ink, #111) 35%, var(--rule, #e5e7eb));
}
.queries-search:focus-within,
.queries-search.active {
  border-color: var(--mint-deep, #0f766e);
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--mint-deep, #0f766e) 18%, transparent);
}
.queries-search svg {
  width: 13px;
  height: 13px;
  flex-shrink: 0;
}
.queries-search-input {
  appearance: none;
  -webkit-appearance: none;
  flex: 1 1 auto;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  color: var(--card-ink, #111);
  font-family: inherit;
  font-size: 12px;
  padding: 0;
}
.queries-search-input::placeholder {
  color: var(--card-muted, #6b7280);
}
/* Strip WebKit's native search-control decorations so our explicit
 * clear button is the only affordance, matching the SPA's search
 * input treatment. */
.queries-search-input::-webkit-search-decoration,
.queries-search-input::-webkit-search-cancel-button,
.queries-search-input::-webkit-search-results-button,
.queries-search-input::-webkit-search-results-decoration {
  -webkit-appearance: none;
  appearance: none;
}
.queries-search-clear {
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: var(--rule-soft, rgba(0, 0, 0, 0.06));
  color: var(--card-muted, #6b7280);
  font-size: 12px;
  line-height: 1;
  cursor: pointer;
  flex-shrink: 0;
}
.queries-search-clear:hover {
  background: var(--card-ink, #111);
  color: var(--card, #fff);
}

/* Inline-params toggle — checkbox + label, sits to the right of the
 * search input. The native checkbox is small but legible; no custom
 * switch chrome here, deliberately, so the control reads as "a setting"
 * rather than "a primary action". */
.queries-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: var(--card-muted, #6b7280);
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
}
.queries-toggle input {
  margin: 0;
  cursor: pointer;
}
.queries-toggle:hover {
  color: var(--card-ink, #111);
}

/* Duplicate badge — sits inline in the meta row before the Copy
 * button. Compact pill with a count; tooltip-on-hover spells out
 * "this query shape ran N times" for the cue-blind. */
.query-entry-dup {
  display: inline-flex;
  align-items: center;
  padding: 1px 7px;
  border-radius: 999px;
  background: color-mix(in srgb, var(--mint-deep, #0f766e) 14%, transparent);
  color: var(--mint-deep, #0f766e);
  font-weight: 600;
  font-size: 10.5px;
  letter-spacing: 0.02em;
  cursor: help;
}
/* Cache-hit badge — same pill geometry as the duplicate badge so the two
 * sit together without arguing, but a cool tint rather than the dup badge's
 * mint. Deliberately quieter than the status word: "this was cached" is
 * context for the row, not its headline. */
.query-entry-cached {
  display: inline-flex;
  align-items: center;
  padding: 1px 7px;
  border-radius: 999px;
  background: color-mix(in srgb, var(--primary, #2563eb) 12%, transparent);
  color: color-mix(in srgb, var(--primary, #2563eb) 82%, var(--card-ink, #111));
  font-weight: 600;
  font-size: 10.5px;
  letter-spacing: 0.02em;
  cursor: help;
}
/* Header stat lifts to the same tint once at least one query hit, so a
 * glance at the header distinguishes "cache is working" from a flat 0. */
.queries-stat-cached strong {
  color: color-mix(in srgb, var(--primary, #2563eb) 82%, var(--card-ink, #111));
}
.queries-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.query-entry {
  border: 1px solid var(--rule, #e5e7eb);
  border-radius: var(--radius, 8px);
  padding: 10px 12px;
  background: var(--card, #fff);
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.query-entry[data-status="pending"] {
  border-color: var(--rule-soft, rgba(0, 0, 0, 0.08));
}
.query-entry[data-status="error"] {
  border-color: var(--neg, #b00020);
}
/* Slow-query tint — amber left-edge stripe + subtle background wash.
 * Threshold lives in SLOW_QUERY_THRESHOLD_MS (top of script). The
 * `data-status="error"` border wins on rows that are both slow and
 * errored — an erroring 4-second query should read as an error first,
 * "happens to be slow" second. */
.query-entry[data-slow="true"] {
  border-left: 3px solid #b45309;
  background: linear-gradient(
    90deg,
    color-mix(in srgb, #b45309 6%, transparent),
    var(--card, #fff) 40%
  );
}
.query-entry[data-slow="true"][data-status="error"] {
  border-left-color: var(--neg, #b00020);
}
.query-entry-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 10px;
  font-size: 11.5px;
  font-family: var(--mono, ui-monospace, monospace);
}
.query-entry-idx {
  font-weight: 600;
  color: var(--card-muted, #6b7280);
}
.query-entry-status {
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 600;
}
.query-entry[data-status="pending"] .query-entry-status {
  color: var(--card-muted, #6b7280);
}
.query-entry[data-status="ok"] .query-entry-status {
  color: var(--pos, #146c43);
}
.query-entry[data-status="error"] .query-entry-status {
  color: var(--neg, #b00020);
}
.query-entry-dim {
  color: var(--card-muted, #6b7280);
}
/* Copy-SQL button — sits at the trailing edge of the meta row. The
 * `margin-left: auto` is what pins it right; everything before it
 * cluster-aligns to the left. Subdued by default; the "Copied" state
 * swaps to the positive tint as the success cue. */
.query-entry-copy {
  margin-left: auto;
  appearance: none;
  font-family: inherit;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  padding: 3px 9px;
  border-radius: 4px;
  border: 1px solid var(--rule, #e5e7eb);
  background: var(--card, #fff);
  color: var(--card-ink, #111);
  cursor: pointer;
  transition:
    background-color 120ms,
    border-color 120ms,
    color 120ms;
}
.query-entry-copy:hover {
  background: var(--rule-soft, rgba(0, 0, 0, 0.04));
  border-color: var(--card-muted, #6b7280);
}
.query-entry-copy:focus-visible {
  outline: 2px solid var(--primary, #2563eb);
  outline-offset: 1px;
}
/* Brief positive-tint flash while the "Copied" label is showing.
 * Matches the `query-entry-status[data-status="ok"]` colour so the
 * success cue reads as the same visual language. The flash state is
 * targeted via `aria-label`: the button's label swaps to "SQL copied
 * to clipboard" for the duration of the flash, and the attribute
 * selector picks that up without needing a separate state class. */
.query-entry-copy[aria-label="SQL copied to clipboard"] {
  background: color-mix(in srgb, var(--pos, #146c43) 12%, transparent);
  border-color: color-mix(in srgb, var(--pos, #146c43) 40%, transparent);
  color: var(--pos, #146c43);
}
.query-entry-sql,
.query-entry-params {
  margin: 0;
  padding: 8px 10px;
  background: var(--rule-soft, rgba(0, 0, 0, 0.04));
  border-radius: 4px;
  overflow-x: auto;
  font-family: var(--mono, ui-monospace, monospace);
  font-size: 12px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
}
.query-entry-params {
  background: transparent;
  padding: 4px 0 0;
  color: var(--card-muted, #6b7280);
}
.query-entry-error {
  margin: 0;
  color: var(--neg, #b00020);
  font-size: 12px;
}
</style>